/**
 * <glossary-terms> — search, tag filter, sort and pagination for the Glossary of Terms page.
 *
 * Port of the old theme's GlossaryFilter (jQuery + Handlebars) as a vanilla custom element.
 *
 * Shopify's server-rendered article rows are the source of truth. This is important because
 * Liquid resolves each article's translated title and body for the active storefront locale,
 * while the shared Algolia articles index only contains its indexed source-language fields.
 * The glossary is bounded, so search, tag filtering, sorting and display pagination all run
 * locally against the complete set of rendered rows.
 */

const DEBOUNCE_MS = 200;
const DEFAULT_SORT = 'title_asc';

function normalizeGlossarySearchText(value) {
  return String(value || '').toLocaleLowerCase();
}

function glossaryEntryMatches(hit, query, selectedTags) {
  const queryParts = normalizeGlossarySearchText(query).split(/\s+/).filter(Boolean);
  const matchesQuery = queryParts.every((part) => hit.searchText.includes(part));
  const matchesTags = selectedTags.size === 0 || hit.tags.some((tag) => selectedTags.has(tag));
  return matchesQuery && matchesTags;
}

class GlossaryTerms extends HTMLElement {
  #debounce = 0;
  #page = 1;
  #hits = [];
  #sourceHits = [];
  #firstLoad = true;

  connectedCallback() {
    this.form = this.querySelector('.glossary-terms__filter-form');
    this.input = this.querySelector('.glossary-terms__search-input');
    this.list = this.querySelector('.glossary-terms__list');
    this.appliedFilters = this.querySelector('.glossary-terms__applied-filters');
    this.emptyState = this.querySelector('.glossary-terms__empty');
    this.pagination = this.querySelector('.glossary-terms__pagination');

    if (!this.list) return;

    this.pageSize = parseInt(this.dataset.pageSize, 10) || 32;
    this.emptyText = this.dataset.emptyText || '';
    this.loadMoreText = this.dataset.loadMoreText || 'Load More';

    // Keep a translated clone of every Liquid-rendered article. Each result is rebuilt from
    // its own row rather than a generic template so localized body markup is never replaced
    // with the source-language fields from Algolia.
    const sourceRows = [...this.list.querySelectorAll('.glossary-terms__item')];
    const templateRow = sourceRows[0];
    this.templateRow = templateRow ? templateRow.cloneNode(true) : null;
    this.#sourceHits = sourceRows.map((row) => this.#readSourceRow(row));

    this.addEventListener('click', this.#handleClick);
    document.addEventListener('click', this.#handleOutsideClick);
    this.addEventListener('keydown', this.#handleKeydown);
    this.form?.addEventListener('submit', (event) => event.preventDefault());
    this.input?.addEventListener('input', this.#handleInput);
    this.addEventListener('change', this.#handleChange);

    this.#update();
  }

  disconnectedCallback() {
    clearTimeout(this.#debounce);
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener('click', this.#handleOutsideClick);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.input?.removeEventListener('input', this.#handleInput);
    this.removeEventListener('change', this.#handleChange);
  }

  /* ---------------------------------------------------------------- state readers */

  get #sort() {
    return this.querySelector('input[name="sort_by"]:checked')?.value || DEFAULT_SORT;
  }

  get #tagInputs() {
    return [...this.querySelectorAll('input[type="checkbox"][name="tags"]')];
  }

  get #selectedTags() {
    return this.#tagInputs.filter((input) => input.checked && input.value);
  }

  get #query() {
    return this.input?.value.trim() || '';
  }

  /* ---------------------------------------------------------------- event handlers */

  #handleInput = () => {
    clearTimeout(this.#debounce);
    this.#debounce = setTimeout(() => {
      this.#page = 1;
      this.#update();
    }, DEBOUNCE_MS);
  };

  #handleChange = (event) => {
    const target = event.target;
    if (!target.matches('input[name="tags"], input[name="sort_by"]')) return;

    // "All Topics" is the empty-valued checkbox: picking it clears the specific tags, and
    // picking a specific tag clears it.
    if (target.matches('input[name="tags"]')) {
      if (target.value === '' && target.checked) {
        this.#tagInputs.forEach((input) => {
          if (input !== target) input.checked = false;
        });
      } else if (target.value !== '' && target.checked) {
        const all = this.#tagInputs.find((input) => input.value === '');
        if (all) all.checked = false;
      }
    }

    this.#page = 1;
    this.#update();
  };

  #handleClick = (event) => {
    const toggle = event.target.closest('.glossary-terms__nav-toggle');
    if (toggle && this.contains(toggle)) {
      event.preventDefault();
      const item = toggle.closest('.glossary-terms__nav-item');
      this.#setOpen(item, !item.classList.contains('is-open'));
      return;
    }

    const chip = event.target.closest('.glossary-terms__chip');
    if (chip && this.contains(chip)) {
      event.preventDefault();
      const input = this.querySelector(`#${CSS.escape(chip.dataset.inputId)}`);
      if (input) {
        input.checked = false;
        this.#page = 1;
        this.#update();
      }
      return;
    }

    const loadMore = event.target.closest('.js-load-more');
    if (loadMore && this.contains(loadMore)) {
      event.preventDefault();
      this.#page += 1;
      this.#update({ append: true });
    }
  };

  #handleOutsideClick = (event) => {
    if (this.contains(event.target)) return;
    this.querySelectorAll('.glossary-terms__nav-item.is-open').forEach((item) =>
      this.#setOpen(item, false)
    );
  };

  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;

    const openItem = this.querySelector('.glossary-terms__nav-item.is-open');
    if (openItem) {
      this.#setOpen(openItem, false);
      openItem.querySelector('.glossary-terms__nav-toggle')?.focus();
      return;
    }

    if (event.target === this.input && this.input.value) {
      event.preventDefault();
      clearTimeout(this.#debounce);
      this.input.value = '';
      this.#page = 1;
      this.#update();
    }
  };

  #setOpen(item, open) {
    if (!item) return;
    item.classList.toggle('is-open', open);
    item
      .querySelector('.glossary-terms__nav-toggle')
      ?.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  /* ---------------------------------------------------------------- querying */

  #update({ append = false } = {}) {
    const selectedTags = new Set(this.#selectedTags.map((input) => input.value));
    this.#hits = this.#sourceHits.filter((hit) =>
      glossaryEntryMatches(hit, this.#query, selectedTags)
    );

    this.#render(append);
    this.#renderAppliedFilters();
    this.#firstLoad = false;
  }

  #readSourceRow(row) {
    let tags = [];
    try {
      tags = JSON.parse(row.dataset.glossaryTags || '[]');
    } catch (_error) {
      tags = [];
    }

    const title = row.dataset.glossaryTitle
      || row.querySelector('.details__header')?.textContent.trim()
      || '';
    const bodyText = row.querySelector('.details-content')?.textContent || '';

    return {
      title,
      tags: Array.isArray(tags) ? tags : [],
      searchText: normalizeGlossarySearchText(`${title} ${bodyText}`),
      row: row.cloneNode(true),
    };
  }

  /* ---------------------------------------------------------------- rendering */

  #sorted() {
    // "relevance" preserves the blog's source order, matching the old theme's
    // "Most Popular" option without replacing localized content.
    if (this.#sort === 'relevance') return this.#hits;

    const direction = this.#sort === 'title_desc' ? -1 : 1;
    const locale = document.documentElement.lang || undefined;
    return [...this.#hits].sort(
      (a, b) =>
        direction *
        (a.title || '').localeCompare(b.title || '', locale, { sensitivity: 'base', numeric: true })
    );
  }

  #render(append) {
    if (!this.templateRow) return;

    const sorted = this.#sorted();
    const end = this.#page * this.pageSize;
    const slice = append ? sorted.slice((this.#page - 1) * this.pageSize, end) : sorted.slice(0, end);

    const fragment = document.createDocumentFragment();
    slice.forEach((hit) => fragment.appendChild(this.#buildRow(hit)));

    if (append) {
      this.list.appendChild(fragment);
    } else {
      this.list.replaceChildren(fragment);
      // A filter change can shorten the page enough to leave the viewport below the
      // results; nudge it back. Not on the initial paint.
      if (!this.#firstLoad) {
        const top = this.list.getBoundingClientRect().top + window.scrollY - 200;
        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
      }
    }

    if (this.emptyState) {
      const isEmpty = sorted.length === 0;
      this.emptyState.textContent = isEmpty ? this.emptyText : '';
      this.emptyState.hidden = !isEmpty;
    }

    this.#renderPagination(sorted.length > end);
  }

  #buildRow(hit) {
    const node = hit.row ? hit.row.cloneNode(true) : this.templateRow.cloneNode(true);

    const details = node.querySelector('.details');
    if (details) details.removeAttribute('open');

    // Wrap local query matches in <em>, which the section stylesheet renders bold.
    const summary = node.querySelector('.details__header');
    if (summary) {
      const title = document.createElement('span');
      title.className = 'glossary-terms__result-title';
      title.innerHTML = this.#query
        ? this.#highlight(hit.title || '', this.#query)
        : this.#escape(hit.title || '');
      [...summary.childNodes].forEach((child) => {
        if (
          child.nodeType === Node.TEXT_NODE ||
          child.classList?.contains('glossary-terms__result-title')
        ) {
          child.remove();
        }
      });
      summary.insertBefore(title, summary.firstChild);
    }

    return node;
  }

  #renderAppliedFilters() {
    if (!this.appliedFilters) return;

    const tags = this.#selectedTags;
    if (!tags.length) {
      this.appliedFilters.replaceChildren();
      this.appliedFilters.hidden = true;
      return;
    }

    const fragment = document.createDocumentFragment();
    tags.forEach((input) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'glossary-terms__chip';
      chip.dataset.inputId = input.id;
      chip.textContent = input.dataset.displayName || input.value;

      const remove = document.createElement('span');
      remove.className = 'glossary-terms__chip-remove';
      remove.setAttribute('aria-hidden', 'true');
      remove.textContent = '✕';
      chip.appendChild(remove);

      fragment.appendChild(chip);
    });

    this.appliedFilters.replaceChildren(fragment);
    this.appliedFilters.hidden = false;
  }

  #renderPagination(hasMore) {
    if (!this.pagination) return;

    if (!hasMore) {
      this.pagination.replaceChildren();
      return;
    }

    let button = this.pagination.querySelector('.js-load-more');
    if (!button) {
      button = document.createElement('a');
      button.className = 'js-load-more size-style ocs-button ocs-button--outline-black';
      button.setAttribute('role', 'button');
      button.href = '#';
      button.textContent = this.loadMoreText;
      this.pagination.appendChild(button);
    }
    button.dataset.page = String(this.#page + 1);
  }

  #highlight(title, terms) {
    if (!terms) return this.#escape(title);
    const parts = terms
      .split(/\s+/)
      .filter(Boolean)
      .map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    if (!parts.length) return this.#escape(title);

    const expression = new RegExp(`(${parts.join('|')})`, 'gi');
    let output = '';
    let lastIndex = 0;
    let match;
    while ((match = expression.exec(title)) !== null) {
      output += this.#escape(title.slice(lastIndex, match.index));
      output += `<em>${this.#escape(match[0])}</em>`;
      lastIndex = match.index + match[0].length;
      if (match.index === expression.lastIndex) expression.lastIndex += 1;
    }
    output += this.#escape(title.slice(lastIndex));
    return output;
  }

  #escape(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

if (!customElements.get('glossary-terms')) {
  customElements.define('glossary-terms', GlossaryTerms);
}
